import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import TrashIcon from '@icons/TrashIcon';
import PhotoUpload from '@molecules/PhotoUpload';
import { BodySmall, ButtonNoStyles, H4, LabelLarge, PhotoUploadWrapper } from '@styled';

// Functionality
import usePropertyPhotos from '../../../hooks/usePropertyPhotos';
import colors from '@general/colors';

const ImagePreviewWrapper = styled.div`
    background-size: cover;
    height: 100%;
    position: relative;
    width: 100%;
`;

const ImagePreviewIconContainer = styled.div`
    background-color: ${colors.ultraLightGray};
    border-radius: 50%;
    height: 40px;
    position: absolute;
    right: 10px;
    top: 10px;
    width: 40px;
`;

const CoverPhotoBanner = styled.div`
    background-color: ${colors.lightGray};
    bottom: 0;
    left: 0;
    overflow: hidden;
    padding: 9px 12px;
    position: absolute;
    width: 100%;
`;

const PropertyPhotos = ({ propertyId, photos: photosInitial }) => {
    const [photos, setPhotos] = useState(photosInitial);
    const { coverPhoto, additionalPhotos, addPropertyPhoto, removePropertyPhoto } = usePropertyPhotos(
        propertyId,
        photos
    );

    return (
        <div>
            <H4 className="mb-0.75rem">Photos</H4>
            <BodySmall className="mb-1.75rem">Accepts all image formats. Limit 10 MB file size.</BodySmall>

            <div className="row">
                <div className="col-12 col-md-3 col-lg-4 mb-4">
                    {coverPhoto?.url ? (
                        <PhotoUploadWrapper className="bg-ultra-light-gray">
                            <ImagePreviewWrapper style={{ backgroundImage: `url(${coverPhoto.url})` }}>
                                <ButtonNoStyles
                                    onClick={async () => {
                                        const allPhotos = await removePropertyPhoto({
                                            photo: coverPhoto,
                                            isCoverPhoto: true,
                                        });

                                        setPhotos(allPhotos);
                                    }}
                                >
                                    <ImagePreviewIconContainer>
                                        <TrashIcon />
                                    </ImagePreviewIconContainer>
                                </ButtonNoStyles>
                            </ImagePreviewWrapper>

                            <CoverPhotoBanner>
                                <LabelLarge>Cover Photo</LabelLarge>
                            </CoverPhotoBanner>
                        </PhotoUploadWrapper>
                    ) : (
                        <PhotoUpload
                            title="Cover Photo"
                            fileTypes="image/*"
                            maxSize={10000000}
                            maxFiles={1}
                            banner
                            onDrop={async files => {
                                const allPhotos = await addPropertyPhoto({ files, isCoverPhoto: true });
                                setPhotos(allPhotos);
                            }}
                        />
                    )}
                </div>

                {additionalPhotos.map(photo => (
                    <div key={photo.recordId} className="col-12 col-md-3 col-lg-4 mb-4">
                        <PhotoUploadWrapper className="bg-ultra-light-gray">
                            <ImagePreviewWrapper style={{ backgroundImage: `url(${photo.url})` }}>
                                <ButtonNoStyles
                                    onClick={async () => {
                                        const allPhotos = await removePropertyPhoto({ photo, isCoverPhoto: false });
                                        setPhotos(allPhotos);
                                    }}
                                >
                                    <ImagePreviewIconContainer>
                                        <TrashIcon />
                                    </ImagePreviewIconContainer>
                                </ButtonNoStyles>
                            </ImagePreviewWrapper>
                        </PhotoUploadWrapper>
                    </div>
                ))}

                <div className="col-12 col-md-3 col-lg-4 mb-4">
                    <PhotoUpload
                        title="Add Photos"
                        fileTypes="image/*"
                        maxSize={10000000}
                        maxFiles={0}
                        multiple
                        onDrop={async files => {
                            const allPhotos = await addPropertyPhoto({ files, isCoverPhoto: false });
                            setPhotos(allPhotos);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

PropertyPhotos.propTypes = {
    propertyId: PropTypes.string.isRequired,
    photos: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            recordId: PropTypes.number.isRequired,
            isCoverPhoto: PropTypes.bool.isRequired,
            url: PropTypes.string.isRequired,
        })
    ),
};

export default PropertyPhotos;
